import { computed, onMounted, ref, watch } from 'vue';
import { useI18N } from '@/plugins/i18n';
import { useStore } from '@/plugins/vuex';
import { TeamRequest } from '@/shared/model/team-request.model';
import { useRouter } from '@/plugins/router';

type ModalProps = {
  title: string;
  primaryButtonText: string;
};

export default {
  props: {
    isOpen: {
      type: Boolean,
      required: true
    },
    onClose: {
      type: Function,
      required: true
    },
    teamRequest: {
      type: Object as () => TeamRequest,
      required: true
    },
    action: {
      type: Function as (teamRequestName: string, initDate: string, endDate: string, jobDescription: string) => void,
      required: true
    },
    customProps: {
      type: Object as () => ModalProps,
      default: () => ({
        title: 'sModalTeamRequestConfirmation.title',
        primaryButtonText: 'sModalTeamRequestConfirmation.label.confirmBtn',
        initDateRequired: true,
        endDateRequired: false
      })
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  setup(props) {
    const store = useStore();
    const i18n = useI18N();
    const router = useRouter();
    const isManager = computed(() => store.getters.account?.authorities.includes('ROLE_GESTOR') ?? false);
    const isInitDateCalendarOpen = ref(false);
    const isEndDateOpenCalendar = ref(false);
    const isEndDateInputHidden = ref(!props.customProps.endDateRequired);
    const teamRequestNameRef = ref(undefined);
    const initDateRef = ref(undefined);
    const endDateRef = ref(undefined);
    const teamRequestDescriptionRef = ref(undefined);
    const isButtonDisabled = ref(true);
    const formattedInitDate = computed(() => {
      if (!initDateRef) return null;
      return formatDate(initDateRef.value);
    });
    const formattedEndDate = computed(() => {
      if (!endDateRef) return null;
      return formatDate(endDateRef.value);
    });

    watch(
      () => props.teamRequest,
      () => {
        teamRequestNameRef.value = props.teamRequest?.name;
        teamRequestDescriptionRef.value = props.teamRequest?.jobDescription;
        initDateRef.value = toDateString(props.teamRequest?.initDate);
        endDateRef.value = toDateString(props.teamRequest?.endDate);
        if (endDateRef.value) isEndDateInputHidden.value = false;
      },
      { immediate: true, deep: true }
    );
    const isFormValid = () => {
      const startDate = initDateRef.value ? new Date(initDateRef.value) : null;
      const endDate = endDateRef.value ? new Date(endDateRef.value) : null;
      const isDateValid = !endDate || (startDate && endDate > startDate);

      if (props.customProps.initDateRequired && props.customProps.endDateRequired) {
        isButtonDisabled.value = !(teamRequestNameRef.value && initDateRef.value && endDateRef.value && isDateValid);
      } else if (props.customProps.initDateRequired) {
        isButtonDisabled.value = !(teamRequestNameRef.value && initDateRef.value && isDateValid);
      } else if (props.customProps.endDateRequired) {
        isButtonDisabled.value = !(teamRequestNameRef.value && endDateRef.value && isDateValid);
      } else {
        isButtonDisabled.value = !(teamRequestNameRef.value && isDateValid);
      }
    };

    const getMinInitDate = () => {
      const baseDate = props.teamRequest?.initDate || new Date().toISOString();
      return adjustDate(baseDate, isManager.value ? -70 : 1);
    };

    watch(
      () => props.customProps.initDateRequired,
      () => {
        isFormValid();
      },
      { immediate: true, deep: true }
    );

    watch(
      () => props.customProps.endDateRequired,
      () => {
        isFormValid();
      },
      { immediate: true, deep: true }
    );

    watch([teamRequestNameRef, initDateRef, endDateRef], isFormValid, { immediate: true });

    watch(
      [teamRequestNameRef, initDateRef, endDateRef, teamRequestDescriptionRef],
      ([name, initDate, endDate, jobDescription]) => {
        if (router.currentRoute.name !== 'teamRequestByIdManager') {
          store.dispatch('checkoutStore/updateMutableTeamRequest', {
            name,
            initDate,
            endDate,
            jobDescription
          });
        }
      },
      { immediate: true }
    );

    const restoreFormFromStore = () => {
      const persisted = store.state.checkoutStore?.mutableTeamRequest;
      const hasValue = persisted && Object.values(persisted).some(v => v !== null && v !== undefined && v !== '');
      if (hasValue) {
        teamRequestNameRef.value = persisted.name || '';
        initDateRef.value = toDateString(persisted.initDate) || null;
        endDateRef.value = toDateString(persisted.endDate) || null;
        teamRequestDescriptionRef.value = persisted.jobDescription || '';
        if (persisted.endDate) isEndDateInputHidden.value = false;
      } else {
        teamRequestNameRef.value = props.teamRequest?.name;
        teamRequestDescriptionRef.value = props.teamRequest?.jobDescription;
        initDateRef.value = toDateString(props.teamRequest?.initDate);
        endDateRef.value = toDateString(props.teamRequest?.endDate);
        if (props.teamRequest?.endDate) isEndDateInputHidden.value = false;
      }
    };

    onMounted(restoreFormFromStore);
    watch(
      () => props.isOpen,
      open => {
        if (open) restoreFormFromStore();
      }
    );

    const closeInitDateCalender = () => {
      isInitDateCalendarOpen.value = false;
    };

    const closeEndDateCalender = () => {
      isEndDateOpenCalendar.value = false;
    };

    const formatDate = (date: string) => {
      if (!date) return null;
      return i18n.d(new Date(date), 'long').replace(/ de /g, ' ');
    };

    const hideEndDateInput = () => {
      isEndDateInputHidden.value = true;
      endDateRef.value = null;
      closeEndDateCalender();
    };

    const openEndDateInput = () => {
      isEndDateInputHidden.value = false;
    };

    const adjustDate = (dateText: string, days: number) => {
      if (!dateText) return null;
      const date = new Date(dateText);
      date.setDate(date.getDate() + days);
      return date.toISOString();
    };

    const onClick = () => {
      if (isButtonDisabled.value) {
        console.error('Form is not valid');
        return;
      }
      // Do NOT close the modal here. Let the parent handle closing after backend success.
      props.action(teamRequestNameRef.value, initDateRef.value, endDateRef.value, teamRequestDescriptionRef.value);
    };

    // Helper para forzar string YYYY-MM-DD
    function toDateString(val: any): string | null {
      if (!val) return null;
      if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}/.test(val)) return val.slice(0, 10);
      const d = new Date(val);
      if (isNaN(d.getTime())) return null;
      return d.toISOString().slice(0, 10);
    }

    return {
      teamRequestNameRef,
      initDateRef,
      endDateRef,
      teamRequestDescriptionRef,
      formattedInitDate,
      formattedEndDate,
      isInitDateCalendarOpen,
      isEndDateOpenCalendar,
      isEndDateInputHidden,
      isButtonDisabled,
      isManager,
      hideEndDateInput,
      openEndDateInput,
      closeInitDateCalender,
      closeEndDateCalender,
      onClick,
      getMinInitDate,
      adjustDate
    };
  }
};
